import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Skeleton matching final geometry: no layout shift.
 *
 * It pulsed from full opacity to 0.35, which on a dark theme is one near-black
 * grey fading towards another. The motion was real and invisible, so a loading
 * page read as four empty boxes - indistinguishable from a page that had
 * finished and found nothing. A sweep is unmistakable at any contrast: it moves
 * across the shape rather than dimming it.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn('relative overflow-hidden rounded bg-panel-2', className)} aria-hidden="true">
      <span className="animate-sweep absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-line-2 to-transparent" />
    </div>
  );
}

/**
 * A wait with a name on it.
 *
 * Every page-level loader was a stack of bare skeletons: no spinner, no words,
 * not even the name of the page being loaded. "I don't know what is going on,
 * no loader" is the accurate description of that, and it is the same screen
 * whether a read takes 200ms or has hung.
 */
export function Loading({ what, rows = 4 }: { what: string; rows?: number }) {
  return (
    <div className="min-h-0 flex-1 space-y-2 p-5">
      <p role="status" className="mb-3 flex items-center gap-2 text-small text-ink-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-acc-2" aria-hidden="true" />
        {what}
      </p>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function Spinner({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <span role="status" className={cn('inline-flex items-center gap-2 text-small text-ink-3', className)}>
      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export interface EmptyStateProps {
  title: string;
  /**
   * For non-success states callers explain, tersely: what happened, what is
   * known, what is unknown, why it matters, next action.
   */
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

/** Deliberate empty or unknown state. Never a blank region. */
export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-10 text-center', className)}>
      <p className="text-body font-medium text-ink">{title}</p>
      {description && <div className="mt-1.5 max-w-md text-small text-ink-3">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * A read that failed, with a way to ask again.
 *
 * Seven surfaces rendered a failed read as one line of warn-coloured prose and
 * nothing else: honest about what happened, and a dead end. A read can fail
 * for a reason that is gone a second later - a dependency restarting, a
 * session refreshing - and the operator's only recourse was to reload the
 * whole page and lose whatever else was on it.
 *
 * `role="alert"` because a failure that appears after the page has settled is
 * announced, not silently drawn.
 */
export function ReadFailed({ what, onRetry }: { what: string; onRetry?: () => void }) {
  return (
    <p role="alert" className="flex flex-wrap items-center gap-2 px-4 py-3 text-small text-warn">
      <span className="min-w-0 flex-1">{what}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="flex-none rounded border border-line bg-panel px-2.5 py-1 text-acc-2 transition-colors duration-fast hover:bg-hov"
        >
          Try again
        </button>
      )}
    </p>
  );
}
